import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { qualityApi, qualityKeys, type AnomalyFilters } from '@/features/quality/api';
import type { DetectInput, QualityMetric } from '@/features/quality/types';

export function useAnomalies(filters: AnomalyFilters) {
  return useQuery({
    queryKey: qualityKeys.anomalies(filters),
    queryFn: () => qualityApi.anomalies(filters),
  });
}

export function useScorecard() {
  return useQuery({
    queryKey: qualityKeys.scorecard,
    queryFn: () => qualityApi.scorecard(),
  });
}

export function useMetricSeries(assetFqn: string, metric: QualityMetric, enabled: boolean) {
  return useQuery({
    queryKey: qualityKeys.metrics(assetFqn, metric),
    queryFn: () => qualityApi.metrics(assetFqn, metric),
    enabled: enabled && assetFqn.length > 0,
  });
}

export function useProfileDataset() {
  return useMutation({
    mutationFn: (datasetId: string) => qualityApi.profileDataset(datasetId),
  });
}

export function useDetect() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: DetectInput) => qualityApi.detect(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['quality', 'anomalies'] }),
  });
}

export function useReviewAnomaly() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'acknowledge' | 'resolve' }) =>
      action === 'acknowledge' ? qualityApi.acknowledge(id) : qualityApi.resolve(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['quality', 'anomalies'] }),
  });
}
